import { FC, useState, useEffect } from "react";
import { useRouter } from "next/router";
import { configureChains, createClient, WagmiConfig } from "wagmi";
import { MetaMaskConnector } from "wagmi/connectors/metaMask";
import { WalletConnectConnector } from "wagmi/connectors/walletConnect";
import { CoinbaseWalletConnector } from "wagmi/connectors/coinbaseWallet";
import { jsonRpcProvider } from "wagmi/providers/jsonRpc";
import { Box, Center, ChakraProvider } from "@chakra-ui/react";
import { Dict } from "@chakra-ui/utils";
import { IFrameEthereumConnector } from "@ledgerhq/ledger-live-wagmi-connector";

import { AppProps } from "next/app";
import Head from "next/head";

import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
// Global CSS
require("../styles/globals.css");

// Global CSS
require("../styles/globals.css");

// Providers
import { AppProvider } from "providers/AppProvider";

// Components
import Sidebar from "components/Sidebar";
import Banner from "components/Banner";
import HandleThemeChange from "components/HandleThemeChange";

// Contants
import {
  menuItems,
  extraItems,
  liquidStakingOptions,
} from "constants/sidebarMenus";
import { ADVISORY } from "constants/constants";

// Firebase Utils
import "utils/firebase";

import { trackEvent } from "utils/firebase";
import { chainConfig } from "config/chain";
import {
  BinanceWalletConnector,
  BitKeepConnector,
  CoinhubConnector,
  Coins98WalletConnector,
  KuCoinWalletConnector,
  MathWalletConnector,
  NaboxConnector,
  OkexConnector,
  SafePalWalletConnector,
  TrustWalletConnector,
} from "config/connectors";

import { DefaultScripts } from "app-head";

const { chains, provider } = configureChains(
  [chainConfig],
  [
    jsonRpcProvider({
      rpc: (chain) => {
        if (chain.id !== chainConfig.id) return null;
        return { http: chain.rpcUrls.default };
      },
    }),
  ]
);

const connectors = [
  new IFrameEthereumConnector({ chains, options: {} }) as any,
  new MetaMaskConnector({
    chains,
    options: {
      shimDisconnect: true,
      shimChainChangedDisconnect: false,
    },
  }),
  new WalletConnectConnector({
    chains,
    options: {
      qrcode: true,
      chainId: chainConfig.id,
    },
  }),
  new BinanceWalletConnector({
    chains,
    options: {
      shimDisconnect: true,
    },
  }),
  new CoinbaseWalletConnector({
    options: {
      appName: "Stader Labs",
    },
  }),
  new Coins98WalletConnector({
    chains,
    options: {
      shimDisconnect: true,
    },
  }),
  new SafePalWalletConnector({
    chains,
    options: {
      shimDisconnect: true,
    },
  }),
  new MathWalletConnector({
    chains,
    options: {
      shimDisconnect: true,
    },
  }),
  new TrustWalletConnector({
    chains,
    options: {
      shimDisconnect: true,
    },
  }),
  new BitKeepConnector({
    chains,
    options: {
      shimDisconnect: true,
    },
  }),
  new OkexConnector({
    chains,
    options: {
      shimDisconnect: true,
    },
  }),
  new NaboxConnector({
    chains,
    options: {
      shimDisconnect: true,
    },
  }),
  new CoinhubConnector({
    chains,
    options: {
      shimDisconnect: true,
    },
  }),
  new KuCoinWalletConnector({
    chains,
    options: {
      shimDisconnect: true,
    },
  }),
];

const wagmiClient = createClient({
  autoConnect: true,
  connectors,
  provider,
});

const DefaultHead = () => {
  return (
    <Head>
      <title>Stader | BNB</title>
      <meta name="description" content="Stake BNB with Stader" />
      <link rel="icon" href="/favicon.ico" />
      <link
        rel="icon"
        type="image/png"
        sizes="32x32"
        href="/favicon-32x32.png"
      />
      <link
        rel="icon"
        type="image/png"
        sizes="16x16"
        href="/favicon-16x16.png"
      />
      <meta
        name="og:description"
        content="Stader's BNB liquid staking gives you a liquid token that gives you max rewards, instant liquidity, and the ability to participate in Defi."
      />
      <meta name="og:title" content="BNB Liquid Staking" />
      <meta />
      <DefaultScripts />
    </Head>
  );
};

const App: FC<AppProps> = ({ Component, pageProps }) => {
  const [showBanner, setShowBanner] = useState(false);
  const [theme, setTheme] = useState<Dict>();

  const closeBanner = () => setShowBanner(false);

  const router = useRouter();

  const isLandingPage = (Component as any).isLandingPage as boolean;

  useEffect(() => {
    const handleComplete = (url: string) => {
      if (url === router.asPath) {
        trackEvent("page_view", {
          page_location: url,
          page_title: document?.title,
        });
      }
    };

    router.events.on("routeChangeComplete", handleComplete);
    router.events.on("routeChangeError", handleComplete);

    return () => {
      router.events.off("routeChangeComplete", handleComplete);
      router.events.off("routeChangeError", handleComplete);
    };
  });

  return (
    <ChakraProvider theme={theme}>
      <HandleThemeChange onChange={setTheme} />
      <DefaultHead />
      <WagmiConfig client={wagmiClient}>
        {!isLandingPage ? (
          <AppProvider>
            {showBanner && (
              <Banner
                text={ADVISORY}
                closeBanner={closeBanner}
                showIcon={false}
                bg="#3182CE"
                color="white"
                fontFamily="regular"
              />
            )}
            <Sidebar
              menuItems={menuItems}
              extraItems={extraItems}
              showBanner={showBanner}
              stakingOptions={liquidStakingOptions}
              minH={{
                base: "auto",
                xl: showBanner ? "calc(100vh - 48px)" : "100vh",
              }}
            >
              <Center>
                <Box w="1280px">
                  <Component {...pageProps} />
                </Box>
              </Center>
            </Sidebar>
          </AppProvider>
        ) : (
          <Component {...pageProps} />
        )}
      </WagmiConfig>
    </ChakraProvider>
  );
};

export default App;
